import { theme } from '@Guidelines/Theme/theme';
import type DocumentTemplatesListView from '@UI/Store/DocumentTemplates/Views/Document/DocumentTemplatesListView';
import type FacilitiesListView from '@UI/Store/Facilities/Views/FacilitiesListView';
import type FacilityTemplateOverrideListView from '@UI/Store/DocumentTemplates/Views/TemplateAssignements/FacilityTemplateOverridesListView';
import { css } from '@emotion/react';
import * as React from 'react';
import TemplateAssignmentOverridesListItem from '@UI/Pages/DocumentTemplates/OrganizationDocumentTemplates/Sections/TemplateAssignements/TemplateAssignmentOverridesList/Item';

const listLabelStyle = css`
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	color: ${theme.colors.inkMuted};
`;

const listStyle = css`
	margin: 0;
	padding: 0;
	list-style: none;
	display: flex;
	flex-direction: column;
	gap: 0.375rem;
`;

type Props = {
	readonly facilityOverridesList: FacilityTemplateOverrideListView;
	readonly templates: DocumentTemplatesListView;
	/** `null` while the fetch has not resolved, or has failed: a per-entry fallback ("Agence n° {id}")
	 * covers both, and a failure here never enters the screen's own `ErrorState`. */
	readonly facilities: FacilitiesListView | null;
	readonly organizationId: number;
	readonly documentTypeId: number;
};

/**
 * Organization-only content appended to a `TemplateAssignmentCard`: which facilities override this
 * document type, with which gabarit, and a way to remove each override from here without needing to
 * be a member of that facility.
 */
const TemplateAssignmentOverridesList: React.FC<Props> = ({
	facilityOverridesList,
	templates,
	facilities,
	organizationId,
	documentTypeId,
}) => {
	const overrides = React.useMemo(
		() => facilityOverridesList.forDocumentTypeId(documentTypeId),
		[facilityOverridesList, documentTypeId],
	);

	if (overrides.length === 0) {
		return <p css={listLabelStyle}>Aucune agence ne surcharge ce type.</p>;
	}

	return (
		<div>
			<p css={listLabelStyle}>Surchargé dans :</p>
			<ul css={listStyle}>
				{overrides.map((override) => (
					<TemplateAssignmentOverridesListItem
						key={override.id}
						facilityOverride={override}
						templates={templates}
						facilities={facilities}
						organizationId={organizationId}
					/>
				))}
			</ul>
		</div>
	);
};

export default TemplateAssignmentOverridesList;
